import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import LeftSideMenu from '../LeftSideMenu';
import { Formik, ErrorMessage } from 'formik';
import { Input, Form as AntdForm, AutoComplete, notification } from 'antd';
import * as Yup from 'yup';
import { Button } from '@ui/components';
import { connect } from 'react-redux';
import { metaSelectors } from '@ui/store/meta';
import { transformFormatSource } from '@ui/tools/transformSource';
import { createMarket, IMarket, updateMarket } from '@ui/api/v2/markets.api';

const { Item } = AntdForm;

const initialValues = {
  name: '',
  city: '',
  address: '',
};

interface IProps {
  market?: IMarket;
  cities: any[];
  onMarketUpdate: (market: IMarket) => void;
  onMarketCreate: (market: IMarket) => void;
  resetEditMarket: () => void;
}

const marketSchema = Yup.object().shape({
  name: Yup.string().required('Поле обязательно к заполнению'),
  city: Yup.string().required('Поле обязательно к заполнению'),
});

function MarketForm({
  cities,
  market,
  onMarketCreate,
  onMarketUpdate,
  resetEditMarket,
}: IProps) {
  const formikRef = useRef<any>(null);

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      if (market && market.id) {
        resetEditMarket();
        const response = await updateMarket(market.id, values);
        if (response.data) {
          onMarketUpdate(response.data);
        }
      } else {
        const { data, error } = await createMarket(values);
        if (data) {
          onMarketCreate(data);
        } else {
          notification.error({
            message: error.response.data.message,
          });
        }
      }
      resetForm();
    },
    [market, onMarketCreate, onMarketUpdate, resetEditMarket],
  );

  const cancelEditForm = useCallback(
    (_, { resetForm }) => {
      resetForm();
      resetEditMarket();
    },
    [resetEditMarket],
  );

  const dataSource = useMemo(() => transformFormatSource(cities), [cities]);

  useEffect(() => {
    if (formikRef.current && market) {
      formikRef.current.setValues({
        name: market.name,
        city: market.city.id.toString(),
        address: market.address,
      });
    }
  }, [market]);

  const title = useMemo(() => {
    if (market && market.id) {
      return 'Редактировать ТЦ';
    }
    return 'Новый ТЦ';
  }, [market]);

  return (
    <LeftSideMenu className="markets__left-side" topPanel={title}>
      <Formik
        ref={formikRef}
        initialValues={initialValues}
        onSubmit={onSubmit}
        onReset={cancelEditForm}
        validationSchema={marketSchema}
      >
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          values,
          setFieldValue,
          handleReset,
        }) => (
          <form onSubmit={handleSubmit} className="markets__form">
            <Item label="Имя">
              <Input
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.name}
                size="large"
                type="text"
                name="name"
              />
              <ErrorMessage
                className="markets__form__error"
                name="name"
                component="div"
              />
            </Item>
            <Item label="Город">
              <AutoComplete
                dataSource={dataSource}
                value={values.city}
                size="large"
                onSelect={value => {
                  setFieldValue('city', value);
                }}
              />
              <ErrorMessage
                name="city"
                className="markets__form__error"
                component="div"
              />
            </Item>
            <Item label="Адрес">
              <Input
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.address}
                size="large"
                type="text"
                name="address"
              />
            </Item>
            <div className="markets__buttons-wrapper">
              <Item>
                <Button type="primary" htmlType="submit">
                  Сохранить
                </Button>
              </Item>
              <Item>
                <Button onClick={handleReset}>Отмена</Button>
              </Item>
            </div>
          </form>
        )}
      </Formik>
    </LeftSideMenu>
  );
}

export default connect(state => ({ cities: metaSelectors.getCities(state) }))(
  memo(MarketForm),
);



// WEBPACK FOOTER //
// ./src/components/Markets/MarketForm.tsx